import { useMemo, useState, type MouseEvent } from "react";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  CircularProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { ok } from "neverthrow";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import { mustBeDefined, mustParse } from "@thalamos/common";
import type { UserDirectoryInstanceUnbranded } from "@thalamos/vision-common";
import {
  ExtendedOrganisationArraySchema,
  UserDirectoryInstanceIdSchema,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../../api/index.js";
import { useToastNotifications } from "../../../hooks/useToastNotifications.js";
import { createRoute } from "../../../routing/createRoute.js";
import { DeleteUserDirectoryInstanceModal } from "./DeleteUserDirectoryInstanceModal.js";

function UserDirectoryInstanceRow({
  instance,
  orgName,
  onEdit,
  onDelete,
}: {
  instance: UserDirectoryInstanceUnbranded;
  orgName: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <TableRow key={instance.id}>
      <TableCell>{orgName}</TableCell>
      <TableCell>
        <Button variant="outlined" onClick={handleClick}>
          {t("admin.userDirectoryInstances.actions")}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
            onClick={() => {
              onEdit();
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" color="primary" />
            </ListItemIcon>
            {t("admin.userDirectoryInstances.edit")}
          </MenuItem>
          <MenuItem
            sx={{
              color: theme.palette.error.main,
              fontWeight: 600,
            }}
            onClick={() => {
              onDelete();
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            {t("admin.userDirectoryInstances.delete")}
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

export const UserDirectoryInstancesManagementInner = ({
  instances,
  orgNameById,
  onAdd,
  onEdit,
  onDelete,
}: {
  instances: UserDirectoryInstanceUnbranded[];
  orgNameById: Map<string, string>;
  onAdd: () => void;
  onEdit: (instance: UserDirectoryInstanceUnbranded) => void;
  onDelete: (instance: UserDirectoryInstanceUnbranded) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [instanceToDelete, setInstanceToDelete] =
    useState<UserDirectoryInstanceUnbranded | null>(null);

  return (
    <Box sx={{ marginTop: "1em" }}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginTop: "1em",
          marginBottom: "1em",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={onAdd}
          sx={{
            height: "3em",
            alignSelf: "center",
            marginRight: "3em",
            width: "15em",
          }}
        >
          {t("admin.userDirectoryInstances.addInstance")}
        </Button>
      </Box>
      <TableContainer>
        <Table size="small" aria-label="user directory instances table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.userDirectoryInstances.organisation")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.userDirectoryInstances.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {instances.map((instance) => (
              <UserDirectoryInstanceRow
                key={instance.id}
                instance={instance}
                orgName={
                  orgNameById.get(instance.organisationId) ??
                  instance.organisationId
                }
                onEdit={() => {
                  onEdit(instance);
                }}
                onDelete={() => {
                  setInstanceToDelete(instance);
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {instanceToDelete && (
        <DeleteUserDirectoryInstanceModal
          open={!!instanceToDelete}
          onClose={() => {
            setInstanceToDelete(null);
          }}
          orgName={
            orgNameById.get(instanceToDelete.organisationId) ??
            instanceToDelete.organisationId
          }
          onConfirm={async () => {
            await onDelete(instanceToDelete);
            setInstanceToDelete(null);
          }}
        />
      )}
    </Box>
  );
};

export function UserDirectoryInstancesManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notifications = useToastNotifications();
  const [{ data, loading }, refetch] = apiHooks.userDirectoryInstance.getAll();
  const [{ data: orgData, loading: orgsLoading }] =
    apiHooks.organisation.search("", 1000);

  const organisations = useMemo(
    () =>
      orgsLoading
        ? []
        : ok(orgData?.results)
            .andThen(mustBeDefined())
            .andThen(mustParse(ExtendedOrganisationArraySchema))
            .unwrapOr([]),
    [orgData?.results, orgsLoading],
  );

  const orgNameById = useMemo(
    () => new Map(organisations.map((org) => [org.id as string, org.name])),
    [organisations],
  );

  const instances: UserDirectoryInstanceUnbranded[] = data ?? [];

  if (loading || orgsLoading) {
    return <CircularProgress />;
  }

  return (
    <UserDirectoryInstancesManagementInner
      instances={instances}
      orgNameById={orgNameById}
      onAdd={() => {
        const newId = UserDirectoryInstanceIdSchema.parse(v4());
        navigate(createRoute.adminUserDirectoryInstanceManagement(newId));
      }}
      onEdit={(instance) => {
        navigate(createRoute.adminUserDirectoryInstanceManagement(instance.id));
      }}
      onDelete={async (instance) => {
        try {
          const response = await api.userDirectoryInstance.delete(instance.id);
          if (response.status === 204) {
            notifications.show(
              t("admin.userDirectoryInstances.deleteSuccess"),
              { severity: "success" },
            );
            void refetch();
          } else {
            notifications.show(t("admin.userDirectoryInstances.deleteError"), {
              severity: "error",
            });
          }
        } catch {
          notifications.show(t("admin.userDirectoryInstances.deleteError"), {
            severity: "error",
          });
        }
      }}
    />
  );
}
